import { useKyc } from "../context/KycProvider";
import { Form, message } from "antd";
import { useNavigate } from "react-router";
import { ApiResponse, kycValues } from "../types/kyc.types";
import { useAPI } from "../../../../hooks/useAPI";
import { ENDPOINTS } from "../../../../constants/endpoints.constant";
import { postCall, getCall } from "../../../../constants/apicalls.constant";
import { useSelector, useDispatch } from "react-redux";
import { User } from "../../../../models/user";
import { RootState, AppDispatch } from "../../../../store/store";
import { logged } from "../../../../store/slices/user.slice";
import { AddressType } from "../../../../models/onboarding";

interface UseKycFormProps {
  isEditMode?: boolean;
  onEditComplete?: () => void;
}

export const useKycForm = (props?: UseKycFormProps) => {
  const kycContext = useKyc();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.user);
  const isEditMode = props?.isEditMode || false;

  const { loading: profileLoading } = useAPI<ApiResponse>(
    ENDPOINTS.kyc_verification,
  );
  const { loading: bankLoading, trigger: triggerBankSubmit } =
    useAPI<ApiResponse>(ENDPOINTS.kyc_verification);
  const { trigger: triggerMe } = useAPI<User>(ENDPOINTS.me);

  const handleSubmit = async (values: kycValues) => {
    // kycContext.setBankDetails(values);
    const chequeFileList = Array.isArray((values as any).blank_cheque_file)
      ? (values as any).blank_cheque_file
      : [];
    const uploadedCheque = chequeFileList[0];

    // In edit mode, file upload is optional
    let bank_cheque_id: string | undefined | any;
    if (uploadedCheque) {
      bank_cheque_id =
        uploadedCheque?.response?.[0]?.uid || uploadedCheque?.uid;
    } else if (!isEditMode) {
      message.error("Blank cheque is required");
      return;
    } else {
      // Use existing file from KYC data
      bank_cheque_id = user?.pendingKycRequest?.bank_cheque_id?.[0]?.uid;
    }

    const panFileList = Array.isArray(values.pan_card_file)
      ? values.pan_card_file
      : [];
    const uploadedPan = panFileList[0];

    // In edit mode, file upload is optional
    let pancard_id: string | undefined;
    if (uploadedPan) {
      pancard_id = uploadedPan?.response?.[0]?.uid || uploadedPan?.uid;
    } else if (!isEditMode) {
      message.error("PAN card is required");
      return;
    } else {
      // Use existing file from KYC data
      pancard_id = user?.pendingKycRequest?.pancard_id?.[0]?.uid;
    }

    if (!bank_cheque_id) {
      message.error(
        uploadedCheque?.status === "uploading"
          ? "Please wait for blank cheque upload to finish."
          : "Blank cheque upload failed or is missing. Please re-upload.",
      );
      return;
    }

    if (!pancard_id) {
      message.error(
        uploadedPan?.status === "uploading"
          ? "Please wait for PAN card upload to finish."
          : "PAN card upload failed or is missing. Please re-upload.",
      );
      return;
    }

    const { pan_card_file, blank_cheque_file, ...restValues } = values;

    const payload = {
      ...restValues,
      bank_cheque_id,
      pancard_id,
      workspace_id: user?.user_workspaces?.[0]?.id,
      user_id: user?.id,
      request_type: "kyc",
    };
    const res = await triggerBankSubmit(postCall(payload));
    if (res?.status) {
      const userRes = await triggerMe(getCall<User>());
      if (userRes?.status && userRes?.data) {
        dispatch(
          logged({
            ...userRes.data,
            ...userRes.data.organization,
            id: userRes.data.id,
            is_verified: userRes.data.is_verified,
            organizer_id: userRes.data.organization?.id,
            address: userRes.data.organization?.address ?? ({} as AddressType),
            workspace: {
              id: userRes.data.workspace?.id,
              name: userRes.data.workspace?.name,
              feature_image: userRes.data.workspace?.feature_image,
              role: userRes.data.workspace?.role,
            },
            user_workspaces: userRes.data.user_workspaces,
            legal_name: userRes.data.organization?.legal_name,
            gst: userRes.data.organization?.gst_details ?? [],
          }),
        );
      }
      message.success("KYC submitted successfully!");
      localStorage.removeItem("kyc_flow_data");

      if (isEditMode && props?.onEditComplete) {
        props.onEditComplete();
      } else {
        navigate("/");
      }
    } else {
      message.error(
        res?.message || "Failed to save bank details. Please try again.",
      );
    }
  };

  return {
    ...kycContext,
    form,
    profileLoading,
    bankLoading,
    handleSubmit,
  };
};
